import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { ListIcon, Search, XIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useSearchParams } from 'react-router';

import type { Pagination } from '@/common/pagination';
import { ROLE } from '@/common/role';
import { AbsoluteLoader } from '@/components/absoluteLoader';
import { BulkActionBar } from '@/components/bulk/BulkActionBar';
import { type AssetListViewMode, MainHeader } from '@/components/MainHeader';
import { DeletedViewBanner } from '@/components/soft-delete/DeletedViewBanner';
import { RestoreConfirmDialog } from '@/components/soft-delete/RestoreConfirmDialog';
import { DELETED_STATUS, StatusFilterSelect } from '@/components/soft-delete/StatusFilterSelect';
import { type SortOption, SortSelect } from '@/components/sort-select';
import { Button } from '@/components/ui/button';
import { useBulkSelection } from '@/hooks/use-bulk-selection';
import { useDebounceValue } from '@/hooks/use-debounce-value';
import { usePermission, usePermissionMetadata } from '@/hooks/use-permission';
import { useRestoreItem } from '@/hooks/use-soft-delete';
import { reportBulkDelete, reportBulkRestore, runBulkDelete, runBulkRestore } from '@/lib/utils/bulk-delete';
import { invalidateAfterSoftDelete } from '@/lib/utils/soft-delete';
import { type AssetListQueryFilterType, getListAssets } from '@/queries/assets/list-assets';
import { getListOrganizationsOption } from '@/queries/organizations/list-organizations';
import { queryClient } from '@/queries/query.client';
import { restoreItem, softDeleteItem, softDeleteTargets } from '@/queries/soft-delete/soft-delete';
import type { ProfileLoaderType } from '@/routes/loader/profile';
import type { AssetWithStats } from '../../../common/asset';
import { AssetCard } from './AssetCard';
import { AssetSkeletonCard } from './AssetCardSkeleton';
import { AssetCompactList, isBulkSelectable } from './AssetCompactList';
import { AssetListMapCard } from './map/AssetListMapCard';
import { AssetsMapContainer } from './map/AssetsMapContainer';

const VIEW_MODE_STORAGE_KEY = 'app-view-mode';
const SHOW_INACTIVE_ORGS_STORAGE_KEY = 'app-show-inactive-organizations';
const ORGANIZATION_FILTER_STORAGE_KEY = 'app-organization-filter';

// Values must use ASSET_SORT_FIELDS from the backend DTO. The organisation option is only offered
// while cards are labelled with their organisation (super admin, all organisations).
const assetSortOptions = (t: TFunction, withOrganization: boolean): SortOption[] => [
	{ value: 'createdAt:desc', label: t('common.sort.newest') },
	{ value: 'createdAt:asc', label: t('common.sort.oldest') },
	{ value: 'name:asc', label: t('common.sort.nameAsc') },
	{ value: 'name:desc', label: t('common.sort.nameDesc') },
	{ value: 'conditionRating:desc', label: t('assets.list.sortConditionDesc') },
	{ value: 'conditionRating:asc', label: t('assets.list.sortConditionAsc') },
	...(withOrganization
		? [{ value: 'organization.name:asc,name:asc', label: t('assets.list.sortOrganizationAsc') }]
		: []),
];
const DEFAULT_SORT = 'createdAt:desc';

const AssetsPageV1 = () => {
	const { t } = useTranslation();
	const { profile } = (useLoaderData() as ProfileLoaderType) || {};
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useDebounceValue(searchTerm, 500);
	// The organization filter and its "Show inactive" switch (list assets of inactive organizations too)
	// are kept in local storage like the view mode, so they survive "Back to List" and the browser's back button.
	const [organizationFilter, setOrganizationFilter] = useState<string>(() => {
		try {
			return localStorage.getItem(ORGANIZATION_FILTER_STORAGE_KEY) || 'all';
		} catch {
			return 'all';
		}
	});
	const [showInactiveOrganizations, setShowInactiveOrganizations] = useState(() => {
		try {
			return localStorage.getItem(SHOW_INACTIVE_ORGS_STORAGE_KEY) === 'true';
		} catch {
			return false;
		}
	});
	const [filters, setFilters] = useState({} as AssetListQueryFilterType);

	const [viewMode, setViewMode] = useState<AssetListViewMode>(() => {
		try {
			const savedMode = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
			if (savedMode === 'grid' || savedMode === 'map' || savedMode === 'list') {
				return savedMode;
			}
		} catch (error) {
			console.error('Error reading view mode from local storage', error);
		}
		return 'grid'; // Default fallback
	});

	// Status "Deleted" (GH #331): per page visit, not remembered. Deleted assets are listed in the compact
	// view only (no card or map view), and only deleted ones, never mixed with active assets.
	const [showDeleted, setShowDeletedState] = useState(false);
	const effectiveViewMode: AssetListViewMode = showDeleted ? 'list' : viewMode;

	const [showSidePanel, setShowSidePanel] = useState(() => window.innerWidth >= 1024);

	// Custom resize state
	const [panelWidth, setPanelWidth] = useState(440);
	const [isResizing, setIsResizing] = useState(false);

	const [pagination, _setPagination] = useState({
		limit: 100,
		offset: 0,
	} as Pagination);

	const { isSuperAdmin } = usePermissionMetadata();
	// Assets of several organizations are listed together: label each card with its organization
	const showOrganization = isSuperAdmin && organizationFilter === 'all';
	const sortOptions = useMemo(() => assetSortOptions(t, showOrganization), [t, showOrganization]);
	const [selectedSort, setSelectedSort] = useState(DEFAULT_SORT);
	// Picking one organization removes the organization sort; fall back to the default instead of a blank select.
	const sort = sortOptions.some((option) => option.value === selectedSort) ? selectedSort : DEFAULT_SORT;
	const {
		data: assetsResponse,
		isLoading,
		isPlaceholderData,
	} = useQuery({
		queryKey: ['assets', 'list', filters, pagination, sort],
		queryFn: () => getListAssets({ ...filters, ...pagination, sort }),
		enabled: !!profile,
		// Keep the current cards on screen while a new sort order loads.
		placeholderData: keepPreviousData,
	});
	const { data: orgListResponse } = useQuery(getListOrganizationsOption({ enabled: isSuperAdmin }));

	const organizations = orgListResponse?.organizations || [];
	const assets = assetsResponse?.assets || [];
	const [selectedAsset, setSelectedAsset] = useState<AssetWithStats | null>(null);

	// Multi-select + bulk delete, offered in the compact list view (cards stay single-action)
	const canDeleteAssets = usePermission(ROLE.ASSETS_DELETE);
	const assetIds = useMemo(() => assets.filter(isBulkSelectable).map((asset) => asset.id as string), [assets]);
	const assetSelection = useBulkSelection(assetIds);
	// Named in the bulk-delete confirmation, so the user sees exactly which assets go
	const selectedAssetNames = assets
		.filter((asset) => assetSelection.selectedIds.includes(asset.id as string))
		.map((asset) => asset.name || (asset.id as string));
	const [isBulkDeleting, setIsBulkDeleting] = useState(false);
	const assetLabel = {
		singular: t('common.entities.asset', { count: 1 }),
		plural: t('common.entities.asset', { count: 2 }),
	};
	const restoreAssets = async (ids: string[]) => {
		const result = await runBulkRestore(ids, (id) => restoreItem(softDeleteTargets.asset(id)));
		reportBulkRestore(result, assetLabel);
		await invalidateAfterSoftDelete(queryClient, result.deleted);
	};
	const handleBulkDeleteAssets = async () => {
		setIsBulkDeleting(true);
		try {
			// The confirmation warned that completed work packages go with their asset
			const result = await runBulkDelete(assetSelection.selectedIds, (id) =>
				softDeleteItem(softDeleteTargets.asset(id), { acceptWorkPackageImpact: true }),
			);
			reportBulkDelete(result, assetLabel, (deletedIds) => void restoreAssets(deletedIds));
			assetSelection.clear();
			await invalidateAfterSoftDelete(queryClient, result.deleted);
		} finally {
			setIsBulkDeleting(false);
		}
	};
	const handleBulkRestoreAssets = async () => {
		setIsBulkDeleting(true);
		try {
			await restoreAssets(assetSelection.selectedIds);
			assetSelection.clear();
		} finally {
			setIsBulkDeleting(false);
		}
	};
	// The rows change completely: a selection of active assets must not reach "Restore selected"
	const setShowDeleted = (next: boolean) => {
		assetSelection.clear();
		setShowDeletedState(next);
	};
	// Status filter (GH #331): assets have no status of their own, so it is Active or Deleted
	const assetStatusFilter = (
		<StatusFilterSelect
			value={showDeleted ? DELETED_STATUS : 'active'}
			onValueChange={(value) => setShowDeleted(value === DELETED_STATUS)}
			options={[{ value: 'active', label: t('common.states.active') }]}
			canShowDeleted
			ariaLabel={t('common.deleted.statusFilter')}
		/>
	);
	const [assetToRestore, setAssetToRestore] = useState<AssetWithStats | null>(null);
	const restoreAsset = useRestoreItem({ onRestored: () => setAssetToRestore(null) });
	const listCardRef = useRef<Record<string, HTMLDivElement | null>>({});

	// The selection is mirrored to `?selected=<id>` so that "Back to List" (and the
	// browser's back button) return to the list with the same asset selected.
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedFromUrl = searchParams.get('selected');
	const appliedUrlSelection = useRef<string | null>(null);
	useEffect(() => {
		if (!selectedFromUrl || isLoading || appliedUrlSelection.current === selectedFromUrl) return;
		const asset = assets.find((item) => item.id === selectedFromUrl);
		if (!asset) return;
		appliedUrlSelection.current = selectedFromUrl;
		setSelectedAsset(asset);
		// Let the current view render its card/row first, then bring it into view
		window.setTimeout(() => {
			listCardRef.current[asset.id || '']?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}, 0);
	}, [selectedFromUrl, assets, isLoading]);

	// Save view mode to local storage whenever it changes
	useEffect(() => {
		try {
			localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
		} catch (error) {
			console.error('Error saving view mode to local storage', error);
		}
	}, [viewMode]);

	// Save the organization filter and "Show inactive" to local storage whenever they change
	useEffect(() => {
		try {
			localStorage.setItem(ORGANIZATION_FILTER_STORAGE_KEY, organizationFilter);
			localStorage.setItem(SHOW_INACTIVE_ORGS_STORAGE_KEY, String(showInactiveOrganizations));
		} catch (error) {
			console.error('Error saving the organization filter to local storage', error);
		}
	}, [organizationFilter, showInactiveOrganizations]);

	// Fall back to all organizations when the selected one is gone (e.g. deleted since it was remembered)
	// or is inactive while "Show inactive" is off — which also covers switching "Show inactive" off
	// with an inactive organization selected.
	useEffect(() => {
		if (!orgListResponse || organizationFilter === 'all') return;
		const remembered = organizations.find((org) => org.id === organizationFilter);
		if (!remembered || (remembered.enabled === false && !showInactiveOrganizations)) {
			setOrganizationFilter('all');
		}
	}, [orgListResponse, organizations, organizationFilter, showInactiveOrganizations]);

	useEffect(() => {
		const newFilters: AssetListQueryFilterType = {};
		if (debouncedSearch) {
			newFilters.search = debouncedSearch;
		}
		// Only super admins have the organization filter; a value remembered in this browser
		// (e.g. from an admin's session) must not narrow another user's list.
		if (isSuperAdmin && organizationFilter !== 'all') {
			newFilters.organizationId = organizationFilter;
		} else if (isSuperAdmin && showInactiveOrganizations) {
			newFilters.includeInactive = true;
		}
		if (showDeleted) {
			newFilters.deleted = true;
		}
		setFilters(newFilters);
	}, [debouncedSearch, organizationFilter, showInactiveOrganizations, isSuperAdmin, showDeleted]);

	const handleSearchTermInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchTerm(value);
		setDebouncedSearch(value);
	};

	const onSelectAsset = useCallback(
		(asset: AssetWithStats | null) => {
			setSelectedAsset(asset);
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (asset?.id) next.set('selected', asset.id);
					else next.delete('selected');
					return next;
				},
				{ replace: true },
			);
			if (asset) {
				listCardRef.current[asset.id || '']?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		},
		[setSearchParams],
	);

	const toggleSidePanel = useCallback(() => {
		setShowSidePanel((prev) => !prev);
	}, []);

	// Custom Resize Logic
	const startResizing = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		setIsResizing(true);
		document.body.style.cursor = 'col-resize';
		document.body.style.userSelect = 'none';
	}, []);

	useEffect(() => {
		if (!isResizing) return;

		const handleMouseMove = (e: MouseEvent) => {
			// Offset left-2 is 8px. Width = Mouse X - 8px.
			// Constrain between 300px and 800px.
			const newWidth = e.clientX - 8;
			setPanelWidth(Math.min(Math.max(newWidth, 300), 800));
		};

		const handleMouseUp = () => {
			setIsResizing(false);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isResizing]);

	return (
		<div className="w-full h-screen flex flex-col overflow-hidden bg-background">
			{/* Main Header (Search, Filters, Toggles) */}
			<MainHeader
				searchTerm={searchTerm}
				onSearchTermChange={handleSearchTermInput}
				organizationFilter={organizationFilter}
				onOrganizationFilterChange={setOrganizationFilter}
				organizations={organizations}
				isSuperAdmin={isSuperAdmin}
				viewMode={effectiveViewMode}
				onViewModeChange={setViewMode}
				viewModeLocked={showDeleted}
				showInactiveOrganizations={showInactiveOrganizations}
				onShowInactiveOrganizationsChange={setShowInactiveOrganizations}
			/>

			{/* Main Content Area - Expands fully */}
			<div className="flex-1 w-full overflow-hidden relative">
				{/* A new sort or filter is loading over the previous cards: keep them from being clicked. */}
				<AbsoluteLoader className="z-40" isLoading={isPlaceholderData} />
				{/* 1. MAP VIEW */}
				{!isLoading && effectiveViewMode === 'map' && (
					<>
						{/* ── MOBILE / TABLET: harita + altta seçili asset kartı ── */}
						<div className="lg:hidden absolute inset-0 flex flex-col">
							<div
								className="relative transition-all duration-300 ease-in-out"
								style={{ height: selectedAsset ? '55%' : '100%' }}
							>
								<AssetsMapContainer assets={assets} selectedAsset={selectedAsset} onSelectAsset={onSelectAsset} />
							</div>
							{selectedAsset && (
								<div className="flex-1 overflow-auto border-t border-border bg-background">
									<div className="flex items-center justify-between px-3 pt-2 pb-1">
										<span className="text-xs text-muted-foreground font-medium">{t('assets.map.selectedAsset')}</span>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 rounded-full hover:bg-muted"
											onClick={() => onSelectAsset(null)}
										>
											<XIcon className="h-4 w-4" />
										</Button>
									</div>
									<div className="px-3 pb-3">
										<AssetListMapCard
											ref={null}
											asset={selectedAsset}
											onSelectAsset={onSelectAsset}
											showOrganization={showOrganization}
											selectedAssetId={selectedAsset.id}
										/>
									</div>
								</div>
							)}
						</div>

						{/* ── DESKTOP: tam ekran harita + yüzen liste paneli ── */}
						<div className="hidden lg:block absolute inset-0 w-full h-full bg-muted/30">
							{/* Floating Frosted Glass List Panel */}
							{showSidePanel && (
								<div
									className="absolute top-2 left-2 bottom-4 z-20 flex flex-col rounded-lg shadow-lg border border-border/50 bg-card/60 backdrop-blur-md overflow-hidden transition-opacity"
									style={{ width: `${panelWidth}px` }}
								>
									{/* Draggable Handle on the Right Edge */}
									<div
										className={`absolute top-0 right-0 bottom-0 w-1.5 cursor-col-resize z-30 transition-colors ${
											isResizing ? 'bg-primary' : 'hover:bg-primary/50 bg-transparent'
										}`}
										onMouseDown={startResizing}
										title={t('assets.map.dragToResize')}
									/>

									{/* Floating Panel Header */}
									<div className="flex items-center justify-between p-3 border-b border-border/50 bg-card/50 shrink-0">
										<span className="text-sm font-semibold text-foreground ml-1">
											{t('assets.list.countLabel', { count: assets.length })}
										</span>
										<SortSelect
											options={sortOptions}
											value={sort}
											onChange={setSelectedSort}
											className="ml-auto mr-1 h-8 lg:min-h-8 lg:w-44"
										/>
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 hover:bg-muted/60 rounded-full text-muted-foreground mr-2"
											onClick={toggleSidePanel}
											title={t('assets.map.closeList')}
										>
											<XIcon className="h-4 w-4" />
										</Button>
									</div>

									{/* Scrollable Asset List */}
									<div className="flex-1 overflow-y-auto p-3 space-y-3 pr-4">
										{assets.length > 0 ? (
											assets.map((asset) => (
												<AssetListMapCard
													ref={(el) => {
														listCardRef.current[asset.id || ''] = el;
													}}
													key={asset.id}
													asset={asset}
													onSelectAsset={onSelectAsset}
													showOrganization={showOrganization}
													selectedAssetId={selectedAsset?.id}
												/>
											))
										) : (
											<p className="text-center text-sm text-muted-foreground mt-10">
												{t('assets.map.emptyLocationData')}
											</p>
										)}
									</div>
								</div>
							)}

							{/* Map Toggle Button (When Panel is Closed) */}
							{!showSidePanel && (
								<Button
									variant="outline"
									size="icon"
									onClick={toggleSidePanel}
									className="absolute top-4 left-4 z-10 bg-card/95 backdrop-blur-sm shadow-md hover:bg-muted/50 border-border text-foreground"
									title={t('assets.list.showList')}
								>
									<ListIcon className="h-4 w-4" />
								</Button>
							)}

							{/* Map Container - Full Width */}
							<div className="w-full h-full">
								<AssetsMapContainer
									assets={assets}
									selectedAsset={selectedAsset}
									onSelectAsset={onSelectAsset}
									showSidePanel={showSidePanel}
									leftPanelWidth={panelWidth}
								/>
							</div>
						</div>
					</>
				)}

				{/* 2. COMPACT LIST VIEW (also the deleted-assets view) */}
				{effectiveViewMode === 'list' && (
					<div className="h-full w-full overflow-y-auto">
						{/* Wider than the grid: a table with every column needs about 1300 px. */}
						<div className="p-6 max-w-[96rem] mx-auto flex flex-col gap-4">
							{!isLoading && (
								<div className="flex flex-wrap items-center justify-between gap-3">
									<span className="text-sm font-semibold text-foreground">
										{t('assets.list.countLabel', { count: assets.length })}
									</span>
									<div className="flex items-center gap-4">
										{canDeleteAssets && assetStatusFilter}
										<SortSelect options={sortOptions} value={sort} onChange={setSelectedSort} />
									</div>
								</div>
							)}
							{showDeleted && <DeletedViewBanner />}
							{canDeleteAssets && assetSelection.count > 0 && (
								<BulkActionBar
									count={assetSelection.count}
									// Only selectable assets count: inactive-org assets have no checkbox
									total={assetIds.length}
									label={assetLabel}
									mode={showDeleted ? 'restore' : 'delete'}
									isDeleting={isBulkDeleting}
									onClear={assetSelection.clear}
									onDelete={showDeleted ? handleBulkRestoreAssets : handleBulkDeleteAssets}
									itemNames={selectedAssetNames}
									warning={t('common.bulk.assetDeleteWarning')}
								/>
							)}
							<AssetCompactList
								assets={assets}
								isLoading={isLoading}
								selectedAssetId={showDeleted ? null : selectedAsset?.id}
								selection={canDeleteAssets ? assetSelection : undefined}
								showOrganization={showOrganization}
								onRestore={showDeleted ? setAssetToRestore : undefined}
							/>
							{assetToRestore && (
								<RestoreConfirmDialog
									open
									onOpenChange={(open) => !open && setAssetToRestore(null)}
									target={softDeleteTargets.asset(assetToRestore.id as string)}
									name={assetToRestore.name ?? t('common.entities.asset', { count: 1 })}
									isRestoring={restoreAsset.isPending}
									onConfirm={() =>
										restoreAsset.mutate({
											target: softDeleteTargets.asset(assetToRestore.id as string),
											name: assetToRestore.name ?? t('common.entities.asset', { count: 1 }),
										})
									}
								/>
							)}
						</div>
					</div>
				)}

				{/* 3. GRID VIEW (Matches Original Main Page) */}
				{effectiveViewMode === 'grid' && (
					<div className="h-full w-full overflow-y-auto">
						<div className="p-6 max-w-7xl mx-auto flex flex-col">
							{!isLoading && (
								<div className="flex flex-wrap items-center justify-between gap-3 mb-4">
									<span className="text-sm font-semibold text-foreground">
										{t('assets.list.countLabel', { count: assets.length })}
									</span>
									<div className="flex items-center gap-4">
										{canDeleteAssets && assetStatusFilter}
										<SortSelect options={sortOptions} value={sort} onChange={setSelectedSort} />
									</div>
								</div>
							)}
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
								{isLoading &&
									Array.from({ length: 6 })
										.fill(1)
										.map((_, index) => (
											<AssetSkeletonCard
												key={`asset-skeleton-${
													// biome-ignore lint/suspicious/noArrayIndexKey: need to use array index for skeleton
													index
												}`}
											/>
										))}
								{!isLoading &&
									assets.map((asset) => (
										<div
											key={asset.id}
											ref={(el) => {
												listCardRef.current[asset.id || ''] = el;
											}}
											className="h-full"
										>
											<AssetCard
												asset={asset}
												selected={selectedAsset?.id === asset.id}
												showOrganization={showOrganization}
											/>
										</div>
									))}
								{!isLoading && assets.length === 0 && (
									<div className="col-span-full text-center py-10">
										<p className="text-muted-foreground">{t('assets.list.emptyFiltered')}</p>
									</div>
								)}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default AssetsPageV1;
